import React from 'react';
import SbEditable, { SbEditableContent } from 'storyblok-react';
import { BlokComponent, NewStoryblokMetaData } from '../../types/blok.model';
import defaultTheme, { darkTheme } from '../../styles/themeVariables';
import RenderAfterPageTransition from '../../old/components/RenderAfterPageTransition';
import { ThemeProvider } from 'styled-components';
import { ThemeModeContextProvider } from '../../util/useThemeMode';
import Spacer from '../components/Spacer';
import { Helmet } from 'react-helmet';
import { graphql, useStaticQuery } from 'gatsby';

export interface AboutPageSchema extends NewStoryblokMetaData {
  body?: SbEditableContent[];
  bodyLower?: SbEditableContent[];
  bodySpacing?: string;
  bodyLowerSpacing?: string;
}

const AboutPage: BlokComponent<AboutPageSchema> = ({ blok, Dynamic }) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            siteUrl
            APP_NAME
            APP_DESCRIPTION
            COMPANY_EMAIL
          }
        }
      }
    `
  );

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    url: site.siteMetadata.siteUrl,
    name: site.siteMetadata.APP_NAME,
    description: site.siteMetadata.APP_DESCRIPTION,
    email: site.siteMetadata.COMPANY_EMAIL,
    telephone: site.siteMetadata.COMPANY_PHONE,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '86-90 Paul Street',
      addressLocality: 'London',
      addressCountry: 'UK',
      addressRegion: 'London',
      postalCode: 'EC2A 4NE',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: site.siteMetadata.COMPANY_EMAIL,
      telephone: site.siteMetadata.COMPANY_PHONE,
      areaServed: 'UK',
    },
  };

  return (
    <SbEditable content={blok}>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>
      <ThemeModeContextProvider themeMode={'light'}>
        <ThemeProvider theme={defaultTheme}>
          <RenderAfterPageTransition>
            {blok.body?.map((insideBlok, index) => (
              <Spacer
                key={insideBlok._uid}
                className={`item-${index} ${
                  insideBlok.spacing ?? blok.bodySpacing ?? 'default'
                } `}
              >
                <Dynamic blok={insideBlok} />
              </Spacer>
            ))}
          </RenderAfterPageTransition>
        </ThemeProvider>
      </ThemeModeContextProvider>
      <ThemeModeContextProvider themeMode={'dark'}>
        <ThemeProvider theme={darkTheme(defaultTheme)}>
          <RenderAfterPageTransition>
            {blok.bodyLower?.map((insideBlok, index) => (
              <Spacer
                key={insideBlok._uid}
                className={`item-${index} ${
                  insideBlok.spacing ?? blok.bodyLowerSpacing ?? 'default'
                } `}
              >
                <Dynamic blok={insideBlok} />
              </Spacer>
            ))}
          </RenderAfterPageTransition>
        </ThemeProvider>
      </ThemeModeContextProvider>
    </SbEditable>
  );
};

export default React.memo(AboutPage);
